import {
  Component,
  signal,
  computed,
  OnInit,
  effect,
  ChangeDetectionStrategy,
  inject,
  ViewChild,
  ElementRef
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SslCheckService } from './ssl-check.service';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';

// ... (existing helper functions kept as is, but we need to insert the class changes)

// -------------------
// Types
// -------------------
interface CPUInfo {
  cores: number | null;
  architecture: string;
}

interface MemoryInfo {
  estimated_totalGB: number | null;
}

interface OSInfo {
  name: string;
  version: string | null;
}

interface BrowserInfo {
  name: string;
  version: string;
}

interface GPUInfo {
  vendor: string;
  renderer: string;
}

interface ScreenInfo {
  width: number;
  height: number;
  pixelRatio: number;
}

interface NetworkInfo {
  type: string;
  effectiveType: string;
  downlinkMbps: number | null;
  rttMs: number | null;
}

interface ClientSpecs {
  userAgent: string;
  deviceType: string;
  cpu: CPUInfo;
  memory: MemoryInfo;
  os: OSInfo;
  browser: BrowserInfo;
  gpu: GPUInfo | null;
  screen: ScreenInfo;
  network: NetworkInfo | null;
}

// -------------------
// Helpers
// -------------------
function detectOSFromUA(ua: string): string {
  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  return 'Unknown';
}

function detectBrowser(ua: string): BrowserInfo {
  const match = ua.match(/(firefox|chrome|safari|edge|opera|edg)\/?\s*(\d+)/i) || [];
  return {
    name: match[1] ?? 'unknown',
    version: match[2] ?? 'unknown',
  };
}

function getGPUInfo(): GPUInfo | null {
  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
  if (!gl) return null;

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (!debugInfo) return null;

  return {
    vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
    renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
  };
}

function flattenSpecs(obj: any, prefix = ''): { label: string; value: any }[] {
  const result: { label: string; value: any }[] = [];

  for (const [key, val] of Object.entries(obj)) {
    const label = prefix ? `${prefix} ${key}` : key;

    if (val && typeof val === 'object' && !Array.isArray(val)) {
      result.push(...flattenSpecs(val, label)); // recurse
    } else {
      result.push({ label, value: val });
    }
  }

  return result;
}

interface TestResult {
  name: string;
  success: boolean;
  message: string;
  details?: any[];
  rttMs?: number;
  collapsed?: boolean; // <-- add this
}

async function generateTurnCredentials(secret: string, realm: string, ttl = 600) {
  const expiry = Math.floor(Date.now() / 1000) + ttl;
  const username = expiry.toString();

  const key = new TextEncoder().encode(secret);
  const data = new TextEncoder().encode(`${username}:${realm}`);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);

  const credential = btoa(String.fromCharCode(...new Uint8Array(signature)));
  return { username, credential };
}

@Component({
  selector: 'app-lmsconnectiontest',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule],
  template: `
    <div class="min-h-full bg-gray-50/50 p-6 md:p-12 font-sans overflow-y-auto">
      <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
        
        <!-- Left Column: Results -->
        <div class="lg:col-span-2 space-y-8" #resultsColumn>
          
          <!-- Header/Logo -->
          <div class="bg-white rounded-[2rem] p-8 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col items-center text-center gap-6 group hover:shadow-2xl transition-all">
             <div class="relative w-24 h-24">
                <div class="absolute inset-0 bg-blue-50 rounded-3xl animate-ping opacity-20"></div>
                <div class="relative w-24 h-24 bg-blue-50 rounded-3xl flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                  <i class="pi pi-bolt text-blue-600 text-4xl"></i>
                </div>
             </div>
             <div>
                <h1 class="text-3xl font-black text-gray-900 m-0 mb-2 uppercase tracking-tighter">LMS Connection Test</h1>
                <p class="text-gray-400 font-bold text-xs uppercase tracking-widest leading-relaxed">System diagnostics & Network verification</p>
             </div>
          </div>

          <!-- Progress / Status -->
          <section class="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 animate-slide-down">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-black text-gray-900 m-0 flex items-center gap-3">
                <span class="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                  <i class="pi pi-chart-line"></i>
                </span>
                Test Progress
              </h2>
              <div class="px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-black uppercase tracking-widest shadow-sm border border-blue-100 animate-pulse">
                {{ statusText() }}
              </div>
            </div>
            
            <div class="relative h-4 bg-gray-100 rounded-full overflow-hidden shadow-inner">
              <div 
                class="absolute top-0 left-0 h-full bg-blue-600 rounded-full transition-all duration-700 shadow-[0_0_15px_rgba(37,99,235,0.4)]"
                [style.width.%]="progress()"
              ></div>
            </div>
            <div class="flex justify-between mt-3 text-[10px] font-black text-gray-300 uppercase tracking-widest">
              <span>Initialized</span>
              <span>{{ progress() }}% Complete</span>
            </div>
          </section>

          <!-- Test Table -->
          <section class="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <table class="w-full border-collapse text-left">
              <thead>
                <tr class="bg-gray-50/50 border-b-2 border-gray-100">
                  <th class="px-6 py-4 text-[0.65rem] font-black text-gray-400 uppercase tracking-widest w-20">Status</th>
                  <th class="px-6 py-4 text-[0.65rem] font-black text-gray-400 uppercase tracking-widest">Test Name</th>
                  <th class="px-6 py-4 text-[0.65rem] font-black text-gray-400 uppercase tracking-widest">Result</th>
                  <th class="px-6 py-4 text-[0.65rem] font-black text-gray-400 uppercase tracking-widest text-right w-32">RTT (ms)</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                @for (result of results(); track $index; let i = $index) {
                  <!-- Main table row -->
                  <tr class="hover:bg-blue-50/30 transition-all duration-300 group">
                    <td class="px-6 py-5">
                      @if (result.success) {
                        <div class="w-8 h-8 bg-green-50 text-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-100/50 ring-1 ring-green-100 animate-bounce-slow">
                          <i class="pi pi-check text-[10px] font-black"></i>
                        </div>
                      } @else {
                        <div class="w-8 h-8 bg-red-50 text-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-100/50 ring-1 ring-red-100 animate-shake">
                          <i class="pi pi-times text-[10px] font-black"></i>
                        </div>
                      }
                    </td>
                    <td class="px-6 py-5">
                      <div class="text-[0.95rem] font-black text-gray-900 group-hover:text-blue-600 transition-colors">{{ result.name }}</div>
                    </td>
                    <td class="px-6 py-5">
                      <div class="text-sm font-medium text-gray-500 leading-relaxed max-w-md">{{ result.message }}</div>
                    </td>
                    <td class="px-6 py-5 text-right">
                      @if (result.rttMs !== undefined && result.rttMs !== null) {
                        <span class="text-xs font-black text-gray-400 group-hover:text-blue-500 transition-colors">{{ result.rttMs }}<span class="text-[8px] ml-0.5 opacity-50">MS</span></span>
                      } @else {
                        <span class="text-gray-200 font-black">-</span>
                      }
                    </td>
                  </tr>

                  <!-- Subtable row for details -->
                  @if (result.details?.length) {
                    <tr class="bg-gray-50/30 border-b border-gray-100">
                      <td colspan="4" class="px-6 py-4">
                        <!-- Toggle button for client specs -->
                        @if (hasClientSpecs(result)) {
                          <button 
                            (click)="collapsedResults[i] = !collapsedResults[i]"
                            class="mb-3 px-4 py-2 bg-white text-[10px] font-black uppercase tracking-widest text-blue-600 rounded-lg shadow-sm ring-1 ring-blue-100 hover:bg-blue-600 hover:text-white transition-all active:scale-95"
                          >
                            <i class="pi {{ collapsedResults[i] ? 'pi-eye' : 'pi-eye-slash' }} mr-2"></i>
                            {{ collapsedResults[i] ? 'Show Details' : 'Hide Details' }}
                          </button>
                        }

                        <!-- Client specs subtable -->
                        @if (!collapsedResults[i] && hasClientSpecs(result)) {
                          <div class="rounded-2xl overflow-hidden border border-gray-100 shadow-xl bg-white animate-slide-down">
                            <table class="w-full text-xs">
                              <thead class="bg-gray-50/50">
                                <tr>
                                  <th class="px-4 py-2 font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Label</th>
                                  <th class="px-4 py-2 font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Value</th>
                                </tr>
                              </thead>
                              <tbody class="divide-y divide-gray-50">
                                @for (d of result.details; track $index) { 
                                  @if ('label' in d) {
                                    <tr class="hover:bg-gray-50 transition-colors">
                                      <td class="px-4 py-2 font-bold text-gray-400 uppercase tracking-tighter">{{ d.label }}</td>
                                      <td class="px-4 py-2 font-black text-gray-900 border-l border-gray-100">{{ d.value }}</td>
                                    </tr>
                                  } 
                                }
                              </tbody>
                            </table>
                          </div>
                        }

                        <!-- ICE candidates subtable -->
                        @if (hasIceCandidates(result)) {
                          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            @for (c of result.details; track $index) { 
                              @if ('type' in c) {
                                <div class="bg-white p-3 rounded-xl shadow-sm ring-1 ring-gray-100 flex items-center gap-3 transition-all hover:shadow-md hover:ring-blue-100 group/item">
                                  <div class="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 font-black text-[10px] group-hover/item:bg-blue-600 group-hover/item:text-white transition-colors">
                                    {{ c.type }}
                                  </div>
                                  <div class="min-w-0">
                                    <div class="text-[9px] font-black text-gray-300 uppercase tracking-widest truncate">{{ c.type }} Candidate</div>
                                    <div class="text-[10px] font-bold text-gray-700 truncate font-mono" [title]="c.candidate">{{ c.candidate }}</div>
                                  </div>
                                </div>
                              } 
                            }
                          </div>
                        }
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </section>
        </div>

        <!-- Right Column: dummy Form & Links -->
        <div class="space-y-8 sticky top-12">
          
          <!-- Useful Links -->
          <section class="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
             <h3 class="text-xl font-black text-gray-900 m-0 mb-6 flex items-center gap-3">
                <span class="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                  <i class="pi pi-link"></i>
                </span>
                Useful Links
              </h3>
              <div class="flex flex-col gap-3">
                <a href="https://greenlight.vantage.ch/rooms/x8y-ik1-w8e-ybx/join" target="_blank" class="flex items-center justify-between p-4 bg-gray-50 text-sm font-bold text-blue-600 rounded-2xl ring-1 ring-gray-100 hover:bg-blue-600 hover:text-white hover:ring-blue-500 transition-all group">
                  <span>Vantage BBB Test Room</span>
                  <i class="pi pi-external-link opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <a href="https://demo.bigbluebutton.org/" target="_blank" class="flex items-center justify-between p-4 bg-gray-50 text-sm font-bold text-blue-600 rounded-2xl ring-1 ring-gray-100 hover:bg-blue-600 hover:text-white hover:ring-blue-500 transition-all group">
                  <span>BBB Official Demo</span>
                  <i class="pi pi-external-link opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
                <a href="https://bigbluebutton.org/tutorials/" target="_blank" class="flex items-center justify-between p-4 bg-gray-50 text-sm font-bold text-blue-600 rounded-2xl ring-1 ring-gray-100 hover:bg-blue-600 hover:text-white hover:ring-blue-500 transition-all group">
                  <span>BBB Info & Tutorials</span>
                  <i class="pi pi-external-link opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
              </div>
          </section>

          <!-- Support Form -->
          <section class="bg-gray-900 text-white rounded-[2.5rem] p-10 shadow-2xl relative overflow-hidden group">
            <!-- Background Decoration -->
            <div class="absolute -right-20 -bottom-20 w-64 h-64 bg-blue-600 rounded-full opacity-10 group-hover:scale-110 transition-transform duration-700 pointer-events-none"></div>
            
            <h3 class="text-2xl font-black m-0 mb-4 tracking-tight relative z-10">Send Results to IT Support</h3>
            <p class="text-gray-400 text-sm font-medium leading-relaxed mb-8 relative z-10 border-l-2 border-blue-500 pl-4">
              Enter your email to send the full test report to the Vantage IT Team for analysis.
            </p>
            
            <form (submit)="sendResults()" class="relative z-10 space-y-6">
              <div class="space-y-2">
                <label for="email" class="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Email Address</label>
                <div class="relative">
                  <i class="pi pi-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"></i>
                  <input 
                    type="email" 
                    id="email" 
                    [(ngModel)]="email" 
                    name="email" 
                    placeholder="you@example.com"
                    class="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white/10 text-sm font-bold transition-all placeholder:text-gray-600" 
                    required 
                    [disabled]="isSending"
                  >
                </div>
              </div>
              
              <button 
                type="submit" 
                class="w-full py-5 bg-blue-600 text-white rounded-[1.5rem] font-black text-sm uppercase tracking-widest shadow-[0_15px_30px_rgba(37,99,235,0.3)] hover:bg-blue-700 hover:shadow-[0_20px_40px_rgba(37,99,235,0.4)] active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed group"
                [disabled]="isSending || !email"
              >
                @if (isSending) {
                  <i class="pi pi-spin pi-spinner mr-3"></i>
                  <span>Sending Report...</span>
                } @else {
                  <i class="pi pi-send mr-3 text-lg group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                  <span>Post Results</span>
                }
              </button>

              @if (sendSuccess) {
                <div class="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3 animate-slide-down">
                   <div class="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white shadow-lg">
                      <i class="pi pi-check text-xs font-black"></i>
                   </div>
                   <p class="text-green-400 text-xs font-black uppercase tracking-tight">Report sent successfully!</p>
                </div>
              }
              @if (sendError) {
                <div class="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 animate-slide-down">
                   <div class="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white shadow-lg">
                      <i class="pi pi-times text-xs font-black"></i>
                   </div>
                   <p class="text-red-400 text-xs font-black uppercase tracking-tight">{{ sendError }}</p>
                </div>
              }
            </form>
          </section>

        </div>
      </div>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LmsConnectionTest implements OnInit {
  private readonly sslCheckService = inject(SslCheckService);
  private readonly http = inject(HttpClient);

  @ViewChild('resultsColumn') resultsColumn!: ElementRef;

  email = '';
  isSending = false;
  sendSuccess = false;
  sendError = '';

  constructor() {
    // Optionally fold any existing rows
    this.results().forEach((_, i) => (this.collapsedResults[i] = true));
    // Log progress whenever completedTests changes
    effect(() => {
      console.log('Progress:', this.completedTests(), '/', this.totalTests, '=', this.progress());
    });
  }

  host = 'bbb.vantage.ch';
  totalTests = 9;

  statusText = signal('Initializing...');
  statusClass = signal('text-gray-500');
  results = signal<TestResult[]>([]);

  // Map to track collapsed state per row
  collapsedResults: Record<number, boolean> = {};

  // Check if a result has client spec rows
  hasClientSpecs(result: TestResult): boolean {
    return result.details?.some((d) => 'label' in d) ?? false;
  }

  // Check if a result has ICE candidates
  hasIceCandidates(result: TestResult): boolean {
    return result.details?.some((d) => 'type' in d) ?? false;
  }
  completedTests = signal(0);
  progress = computed(() => Math.floor((this.completedTests() / this.totalTests) * 100));

  ngOnInit() {
    this.runTests();
  }

  addResult(
    name: string,
    success: boolean,
    message: string,
    details?: any[],
    rttMs?: number,
    meta?: any
  ) {
    this.results.update((arr) => [...arr, { name, success, message, details, rttMs }]);
    this.completedTests.update((v) => v + 1);
    // Automatically fold the new row
    this.collapsedResults[this.results().length - 1] = true;
  }
  async collectFullClientSpecs() {
    // User agent
    const ua = navigator.userAgent;

    // Device type
    const deviceType = /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop';

    // CPU cores
    const cpuCores = navigator.hardwareConcurrency ?? null;

    // Memory (approx)
    const totalMemoryGB = (navigator as any).deviceMemory ?? null;

    // Browser info
    const uaData = (navigator as any).userAgentData;
    const browserInfo = uaData
      ? await uaData.getHighEntropyValues(['platform', 'platformVersion', 'architecture', 'model'])
      : null;

    // OS info
    const osName = browserInfo?.platform ?? detectOSFromUA(ua);
    const osVersion = browserInfo?.platformVersion ?? null;

    // GPU info
    const gpu = getGPUInfo();

    // Screen info
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio;

    // Network info (best-effort)
    const connection = (navigator as any).connection;
    const networkInfo = connection
      ? {
        type: connection.type ?? 'unknown',
        effectiveType: connection.effectiveType ?? 'unknown',
        downlinkMbps: connection.downlink ?? null,
        rttMs: connection.rtt ?? null,
      }
      : null;

    return {
      userAgent: ua,
      deviceType,
      cpu: {
        cores: cpuCores,
        architecture: browserInfo?.architecture ?? 'unknown',
      },
      memory: {
        estimatedTotalGB: totalMemoryGB,
      },
      os: {
        name: osName,
        version: osVersion,
      },
      browser: detectBrowser(ua),
      gpu,
      screen: {
        width: screenWidth,
        height: screenHeight,
        pixelRatio,
      },
      network: networkInfo,
    };
  }

  async runClientSpecsTest(this: any) {
    try {
      const specs = await this.collectFullClientSpecs();

      this.addResult(
        'Client system information',
        true,
        'Collected full client specs',
        flattenSpecs(specs),
        null,
        { collapsed: true } // optional metadata for UI state // <--- flattened here
      );

      return specs;
    } catch (e) {
      this.addResult('Client system information', false, 'Failed to collect client specs', [
        { label: 'Error', value: String(e) },
      ]);
      return null;
    }
  }

  async measureRTT(url: string) {
    const start = performance.now();
    try {
      // fetch a tiny resource
      await fetch(url, { method: 'HEAD', cache: 'no-store', mode: 'no-cors' });
      const rtt = Math.round(performance.now() - start);
      this.addResult('RTT', true, `Realtime timed RTT to ${url}`, [], rtt);
      return rtt;
    } catch {
      return null; // could not reach
    }
  }

  async testDns() {
    console.log('Starting DNS test...');
    const start = performance.now();
    try {
      await fetch('/favicon.png', { method: 'HEAD', mode: 'no-cors' });
      const rtt = Math.round(performance.now() - start);
      this.addResult('DNS Lookup', true, `Resolved ${this.host} (IP unknown in browser)`, [], rtt);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.addResult('DNS Lookup', false, `Failed to resolve ${this.host}: ${message}`);
      return false;
    }
  }

  async testPort(port: number) {
    const protocol = port === 443 ? 'https' : 'http';
    console.log(`Testing ${protocol.toUpperCase()} port ${port}...`);
    const start = performance.now();
    try {
      await fetch(`${protocol}://${this.host}:${port}/favicon.ico`, {
        method: 'HEAD',
        mode: 'no-cors',
      });
      const rtt = Math.round(performance.now() - start);
      this.addResult(
        `${protocol.toUpperCase()} Port ${port}`,
        true,
        `${protocol.toUpperCase()} port ${port} is open`,
        [],
        rtt
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.addResult(
        `${protocol.toUpperCase()} Port ${port}`,
        false,
        `${protocol.toUpperCase()} port ${port} is closed or blocked (${message})`
      );
    }
  }

  async testTurnFingerprint(turnHost: string, expectedFingerprint: string): Promise<boolean> {
    console.log('=== Starting TURN Fingerprint Test ===');
    console.log('TURN Host:', turnHost);

    // Generate valid TURN credentials
    const secret = '95scVe0t23m8NhdXx9Av'; // Your TURN secret
    const ttl = 86400;
    const timestamp = Math.floor(Date.now() / 1000) + ttl;
    const username = `${timestamp}:fingerprinttest`;

    console.log('Generated username:', username);

    // Generate HMAC-SHA1 credential
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secret),
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(username));
    const credential = btoa(String.fromCharCode(...new Uint8Array(signature)));

    console.log('Generated credential:', credential);
    console.log('TURN URL:', `turns:${turnHost}:443?transport=tcp`);

    const pc = new RTCPeerConnection({
      iceServers: [
        {
          urls: `turns:${turnHost}:443?transport=tcp`,
          username: username,
          credential: credential,
        },
      ],
    });

    pc.createDataChannel('probe');

    try {
      const offer = await pc.createOffer();
      console.log('Created offer:', offer.sdp?.substring(0, 200) + '...');
      await pc.setLocalDescription(offer);
      console.log('Set local description successfully');
    } catch (err) {
      console.error('Failed to create/set offer:', err);
      this.addResult('TURN DTLS Fingerprint', false, `Failed to create offer: ${err}`);
      pc.close();
      return false;
    }

    const label = 'TURN DTLS Fingerprint';
    const start = performance.now();

    return new Promise<boolean>((resolve) => {
      let resolved = false;
      let candidateCount = 0;

      // Set timeout in case ICE gathering hangs
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.error('Timeout reached. Candidate count:', candidateCount);
          console.error('ICE Gathering State:', pc.iceGatheringState);
          console.error('ICE Connection State:', pc.iceConnectionState);
          this.addResult(
            label,
            false,
            `Timeout: TURN server unreachable (${candidateCount} candidates gathered)`,
            [],
            Math.round(performance.now() - start)
          );
          pc.close();
          resolve(false);
        }
      }, 10000);

      pc.onicecandidate = async (event) => {
        if (event.candidate) {
          candidateCount++;
          console.log('ICE Candidate #' + candidateCount + ':', event.candidate.candidate);
        }

        // Wait for ICE gathering to complete
        if (pc.iceGatheringState !== 'complete') return;
        if (resolved) return;

        console.log('ICE gathering complete. Total candidates:', candidateCount);

        try {
          const stats = await pc.getStats();
          console.log('Got stats, reports count:', stats.size);

          let actualFingerprint: string | null = null;
          let certificateInfo: any = {};

          // Find the certificate fingerprint from stats
          stats.forEach((report) => {
            console.log('Report type:', report.type, 'ID:', report.id);
            if (report.type === 'certificate') {
              console.log(
                'Certificate report found - ALL PROPERTIES:',
                JSON.stringify(report, null, 2)
              );
              // Log all certificate properties
              console.log('Certificate details:', {
                fingerprint: report.fingerprint,
                fingerprintAlgorithm: report.fingerprintAlgorithm,
                base64Certificate: report.base64Certificate
                  ? report.base64Certificate.substring(0, 100) + '...'
                  : 'N/A',
                issuerCertificateId: report.issuerCertificateId,
              });
            }

            if (report.type === 'certificate' && report.fingerprint) {
              console.log('Fingerprint found:', report.fingerprint);
              // Extract fingerprint - it may or may not have sha-256 prefix
              let fp = report.fingerprint;
              if (fp.startsWith('sha-256 ')) {
                fp = fp.replace('sha-256 ', '');
              } else if (fp.startsWith('SHA-256 ')) {
                fp = fp.replace('SHA-256 ', '');
              }
              actualFingerprint = fp.trim().toUpperCase();
              console.log('Extracted fingerprint:', actualFingerprint);

              // Try to decode certificate details if base64Certificate is available
              if (report.base64Certificate) {
                try {
                  certificateInfo = this.parseCertificateInfo(report.base64Certificate);
                  console.log('Parsed certificate info:', certificateInfo);
                } catch (e) {
                  console.log('Could not process certificate:', e);
                }
              }
            }
          });

          clearTimeout(timeout);
          resolved = true;

          if (!actualFingerprint) {
            console.error('No fingerprint found in stats');
            this.addResult(
              label,
              false,
              'TURN unreachable or no DTLS fingerprint found',
              [],
              Math.round(performance.now() - start)
            );
            pc.close();
            resolve(false);
            return;
          }

          // At this point, actualFingerprint is guaranteed to be a string
          // Store in a const to help TypeScript's type narrowing
          const foundFingerprint: string = actualFingerprint;
          const expectedUpper = expectedFingerprint.toUpperCase().replace(/:/g, '');
          const actualUpper = foundFingerprint.replace(/:/g, '');

          console.log('Expected fingerprint:', expectedUpper);
          console.log('Actual fingerprint:', actualUpper);
          console.log('Match:', actualUpper === expectedUpper);

          if (actualUpper !== expectedUpper) {
            const certDetails = certificateInfo.commonName
              ? ` (CN: ${certificateInfo.commonName}${certificateInfo.organization ? ', O: ' + certificateInfo.organization : ''
              })`
              : '';

            this.addResult(
              label,
              false,
              `⚠️ SSL Decryption Detected! Corporate proxy/firewall is intercepting connections${certDetails}. Expected: ${expectedFingerprint}, Got: ${foundFingerprint}`,
              [
                {
                  expected: expectedFingerprint,
                  actual: foundFingerprint,
                  certificateInfo: certificateInfo,
                },
              ],
              Math.round(performance.now() - start)
            );
            pc.close();
            resolve(false);
          } else {
            this.addResult(
              label,
              true,
              '✓ No SSL Decryption - Fingerprint matches expected value',
              [],
              Math.round(performance.now() - start)
            );
            pc.close();
            resolve(true);
          }
        } catch (err) {
          clearTimeout(timeout);
          resolved = true;
          console.error('Error checking fingerprint:', err);
          this.addResult(
            label,
            false,
            `Error checking fingerprint: ${err}`,
            [],
            Math.round(performance.now() - start)
          );
          pc.close();
          resolve(false);
        }
      };

      pc.onicegatheringstatechange = () => {
        console.log('ICE Gathering State changed to:', pc.iceGatheringState);
        if (pc.iceGatheringState === 'complete' && !resolved) {
          // Trigger the check if onicecandidate didn't fire
          pc.onicecandidate?.(new RTCPeerConnectionIceEvent('icecandidate', { candidate: null }));
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('ICE Connection State changed to:', pc.iceConnectionState);
      };

      pc.onicecandidateerror = (event: any) => {
        console.error('ICE Candidate Error:', {
          errorCode: event.errorCode,
          errorText: event.errorText,
          url: event.url,
          hostCandidate: event.hostCandidate,
        });
      };
    });
  }

  parseCertificateInfo(base64Cert: string): {
    commonName?: string;
    organization?: string;
    issuer?: string;
  } {
    try {
      // Decode base64 to binary
      const binaryString = atob(base64Cert);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert to string for simple pattern matching
      const certText = new TextDecoder('latin1').decode(bytes);

      // Try to find common certificate fields (this is a simplified approach)
      const info: any = {};

      // Look for common patterns in certificate
      // CN (Common Name) is often visible in the certificate
      const cnMatch = certText.match(/[\x00-\xFF]*CN=([^\x00,]+)/);
      if (cnMatch) {
        info.commonName = cnMatch[1].replace(/[\x00-\x1F\x7F-\xFF]/g, '').trim();
      }

      // Try to find organization
      const orgMatch = certText.match(/[\x00-\xFF]*O=([^\x00,]+)/);
      if (orgMatch) {
        info.organization = orgMatch[1].replace(/[\x00-\x1F\x7F-\xFF]/g, '').trim();
      }

      console.log('Parsed certificate info:', info);
      return info;
    } catch (e) {
      console.error('Failed to parse certificate:', e);
      return {};
    }
  }

  // Helper method to get the actual fingerprint from your TURN server
  // You'll need to get this value and hardcode it or fetch it from your backend
  async getExpectedFingerprint(): Promise<string> {
    // Option 1: Hardcode the known fingerprint of your TURN server's certificate
    // You can get this by running: openssl s_client -connect turn1.vantage.ch:443 < /dev/null 2>/dev/null | openssl x509 -fingerprint -sha256 -noout
    return 'CA628C8087394421D28E7073D635D7DC9E4EC96DD9C73EE2F13413D065362C27';

    // Option 2: If you have an API endpoint that returns the fingerprint
    // const response = await fetch('https://bbb.vantage.ch/api/turn-fingerprint');
    // const data = await response.json();
    // return data.fingerprint;
  }

  // ADDED: New test method using the service
  async testSslDecryption(): Promise<boolean> {
    const start = performance.now();
    const label = 'Corporate SSL Decryption Check';

    try {
      //const isDecrypted = await this.sslCheckService.isSslDecryptionActive();
      const isDecrypted = await this.sslCheckService.isSslDecryptionActive('turn1.vantage.ch');
      const rtt = Math.round(performance.now() - start);

      if (isDecrypted) {
        this.addResult(
          label,
          false,
          '⚠️ SSL Decryption (Proxy) Detected! Connection succeeded against untrusted cert.',
          [],
          rtt
        );
        return true; // Return true on detection, as detection is the "success" of the check's goal
      } else {
        this.addResult(
          label,
          true,
          '⚠️ No SSL Decryption (Proxy) Detected! Results unverified, please check the certificate manually.',
          [],
          rtt
        );
        return false;
      }
    } catch (error) {
      const rtt = Math.round(performance.now() - start);
      this.addResult(label, false, `SSL Check Failed (Network Error?): ${error}`, [], rtt);
      return false;
    }
  }

  async testBBBWebRTC() {
    console.log('Starting WebRTC / TURN tests...');

    const turnServers = [
      {
        url: 'turn:turn1.vantage.ch:3478?transport=udp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
      {
        url: 'turn:turn1.vantage.ch:443?transport=tcp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
      {
        url: 'turns:turn1.vantage.ch:443?transport=tcp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
      {
        url: 'turn:turn2.vantage.ch:3478?transport=udp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
      {
        url: 'turn:turn2.vantage.ch:443?transport=tcp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
      {
        url: 'turns:turn2.vantage.ch:443?transport=tcp',
        secret: '95scVe0t23m8NhdXx9Av',
        ttl: 86400,
      },
    ];

    const iceServers: RTCIceServer[] = [];

    for (const s of turnServers) {
      if (s.secret) {
        const ts = Math.floor(Date.now() / 1000) + (s.ttl || 86400);
        const username = `${ts}:localtest`;
        const key = await crypto.subtle.importKey(
          'raw',
          new TextEncoder().encode(s.secret),
          { name: 'HMAC', hash: 'SHA-1' },
          false,
          ['sign']
        );
        const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(username));
        const credential = btoa(String.fromCharCode(...new Uint8Array(signature)));
        iceServers.push({ urls: s.url, username, credential });
      } else {
        iceServers.push({ urls: s.url });
      }
    }

    return new Promise<boolean>((resolve) => {
      const pc = new RTCPeerConnection({ iceServers });
      const candidates: any[] = [];
      const startTime = performance.now();

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const proto = event.candidate.candidate.split(' ')[2]?.toUpperCase() || 'TCP';
          candidates.push({ type: proto, candidate: event.candidate.candidate });
        }
      };

      try {
        pc.createDataChannel('test');
        pc.createOffer()
          .then((o) => pc.setLocalDescription(o))
          .catch(console.error);
      } catch (err) {
        console.error('WebRTC setup failed:', err);
      }

      setTimeout(() => {
        const rtt = Math.round(performance.now() - startTime);
        if (candidates.length === 0) {
          this.addResult('BBB WebRTC Connectivity', false, 'No ICE candidates gathered', [], rtt);
        } else {
          this.addResult(
            'BBB WebRTC Connectivity',
            true,
            `Collected ${candidates.length} ICE candidates`,
            candidates,
            rtt
          );
        }
        pc.close();
        resolve(candidates.length > 0);
      }, 1000);
    });
  }
  async getGeoInfo() {
    try {
      const response = await fetch(
        'https://iplocate.io/api/lookup/?apikey=c3406e4c8c710d275673d47c1d18d664'
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      console.log('Geo data:', data);
      if (data) {
        const flattened = flattenSpecs(data); // reuse your flatten helper
        this.addResult(
          'Geo Data Information',
          true,
          'Fetched client geolocation info',
          flattenSpecs(data),
          0,
          { collapsed: true } // optional metadata for UI state // <--- flattened here
        );
      }
      return data;
    } catch (err) {
      console.error('Failed to fetch geo info:', err);
    }
  }

  async runTests() {
    this.statusText.set('Collecting client specs…');
    const specs = await this.runClientSpecsTest();

    this.statusText.set('Starting DNS test…');
    const dnsSuccess = await this.testDns();

    this.statusText.set('Fetching geolocation info…');
    await this.getGeoInfo();

    if (dnsSuccess) {
      // Check for SSL Decryption/Proxy
      this.statusText.set('Checking for SSL Decryption…');
      const isSslDecryptionActive = await this.testSslDecryption();

      if (isSslDecryptionActive) {
        // If the general SSL check detects a proxy, we can give a warning right away
        this.statusText.set('⚠️ Check results may be misleading.');
        this.statusClass.set('text-orange-600');
      }
      this.statusText.set('Testing RTT BBB…');
      await this.measureRTT('bbb.vantage.ch');

      this.statusText.set('Testing RTT Turn 1…');
      await this.measureRTT('turn1.vantage.ch');

      this.statusText.set('Testing RTT Turn 2…');
      await this.measureRTT('turn2.vantage.ch');

      this.statusText.set('Testing HTTP port 80…');
      await this.testPort(80);

      this.statusText.set('Testing HTTPS port 443…');
      await this.testPort(443);

      this.statusText.set('Testing WebRTC / TURN…');
      await this.testBBBWebRTC();
    } else {
      this.statusText.set('DNS failed; skipping other tests');
    }


    this.statusText.set('All tests completed');
    this.statusClass.set('text-green-600');
  }

  async sendResults() {
    if (!this.email) return;

    this.isSending = true;
    this.sendError = '';
    this.sendSuccess = false;

    // Capture HTML
    let htmlContent = this.resultsColumn.nativeElement.innerHTML;
    // Replace title
    htmlContent = htmlContent.replace('Test Progress', `Test results for ${this.email}`);

    const mail_subject = `Test results for ${this.email}`;
    const mail_sender = 'noreplyzohointakes@vantage.ch';

    // Use the email entered as the CC (or recipient if you prefer, but requirement said "cc is the email filled in" and "recipient = 'vincent.verbeke@vantage.ch'")
    // The requirement said: "recipient = 'vincent.verbeke@vantage.ch', cc is the email filled in"
    // However, the JSON structure provided was:
    // "recipient": email_private,
    // "subject": mail_subject,
    // "message": body_filled
    // And "cc is the email filled in" implies duplicate? Or maybe "recipient" IS the fixed email and we need a 'cc' field?
    // Looking at the example: "data=mail_data ... recipient=vincent.verbeke@vantage.ch"
    // But the prompt says: "mail_data = { "sender": ..., "recipient": email_private, ... }"
    // Wait, the prompt implies "recipient" in the JSON object should be the private email (variable name `email_private`).
    // BUT later it says: "recipient = 'vincent.verbeke@vantage.ch', cc is the email filled in"
    // I will follow the instruction: "recipient = 'vincent.verbeke@vantage.ch'" AND "cc is the email filled in".
    // I'll add a 'cc' field to the JSON if the API supports it, or maybe the user meant "recipient" should be the user's email?
    // Let's re-read carefully: "recipient = 'vincent.verbeke@vantage.ch', cc is the email filled in"
    // AND "mail_data = { "sender": ..., "recipient": email_private, ... }"
    // This is contradictory. `email_private` suggests a variable.
    // I will assume the key "recipient" in the JSON should be 'vincent.verbeke@vantage.ch' and there should be a "cc" key with `this.email`.

    const mail_data = {
      sender: mail_sender,
      recipient: 'vincent.verbeke@vantage.ch',
      cc: this.email,
      subject: mail_subject,
      message: htmlContent
    };

    try {
      await firstValueFrom(this.http.post('https://api.vantage.ch/email/send_single_mail', mail_data));
      this.sendSuccess = true;
      this.isSending = false;
    } catch (error) {
      console.error('Failed to send email:', error);
      this.sendError = 'Failed to send results. Please try again.';
      this.isSending = false;
    }
  }
}
